import { appliedDefaultsVersion, hasSavedGateways, mergeDefaultGateways, normalizeGatewayPreferences } from './homeGateways.js'

const readFailure = 'Your gateway choices are unavailable. Try again later.'
const saveFailure = 'Your choices were not saved. Try again.'
// `customized` records whether the account has ever saved tiles, so the UI can
// offer collection-aware defaults until it does.
const normalize = value => ({ ...normalizeGatewayPreferences(value), customized: hasSavedGateways(value) })

// One store per authenticated identity: neither old responses nor old queued
// edits can populate another account. Edits are functions of the last saved row.
export function createHomeGatewayPreferencesStore(userId, repository) {
  let state = { ...normalize(), loading: Boolean(userId), saving: false, error: '' }
  let active = true
  let generation = 0
  let pending = 0
  let loaded = false
  // Defaults are merged at most once per successful read; refresh re-arms it.
  let defaultsChecked = false
  let tail = Promise.resolve()
  const listeners = new Set()
  const publish = patch => { state = { ...state, ...patch }; listeners.forEach(listener => listener()) }
  const current = token => active && token === generation

  return {
    getSnapshot: () => state,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener) },
    deactivate() { active = false; generation += 1 },
    async refresh() {
      active = true
      const token = ++generation
      loaded = false
      defaultsChecked = false
      publish({ ...normalize(), loading: Boolean(userId), saving: pending > 0, error: '' })
      if (!userId) return
      // A refresh must not read a row ahead of an already-started write.
      await tail
      if (!current(token)) return
      try {
        const data = await repository.read(userId)
        if (!current(token)) return
        loaded = true
        publish({ ...normalize(data), loading: false, error: '' })
      } catch {
        if (current(token)) publish({ ...normalize(), loading: false, error: readFailure })
      }
    },
    // One-time post-load step: offer a saved row the defaults introduced after
    // its stored version. Call only once the collection has definitely loaded,
    // because Accord defaults are derived from it; until then nothing is merged
    // and no version marker is written. The merged tiles render immediately and
    // persist through the same queue as explicit edits. A failed write is not
    // retried until the next refresh, and the next explicit edit persists the
    // merged state anyway because edits build on the current snapshot.
    async applyDefaults(fragrances = []) {
      if (!userId || !active || !loaded || defaultsChecked || !state.customized) return
      defaultsChecked = true
      const token = generation
      const merged = mergeDefaultGateways({
        tiles: state.tiles,
        shelves: state.shelves,
        fragrances,
        appliedVersion: appliedDefaultsVersion(state.shelves),
      })
      if (!merged.changed) return
      publish({ tiles: merged.tiles, shelves: merged.shelves })
      const operation = tail.then(() => {
        if (!current(token)) throw new Error(saveFailure)
        return repository.write(userId, { tiles: merged.tiles, shelves: merged.shelves })
      })
      tail = operation.catch(() => {})
      try { await operation } catch { /* rendered already; retried after the next refresh */ }
    },
    save(edit) {
      if (!userId || !active || !loaded) return Promise.reject(new Error(saveFailure))
      const token = generation
      pending += 1
      publish({ saving: true, error: '' })
      const operation = tail.then(async () => {
        if (!current(token)) throw new Error(saveFailure)
        const { tiles, shelves } = normalize(edit(normalize(state)))
        const data = await repository.write(userId, { tiles, shelves })
        if (!current(token)) throw new Error(saveFailure)
        publish({ ...normalize(data), error: '' })
      }).catch(() => {
        if (current(token)) publish({ error: saveFailure })
        throw new Error(saveFailure)
      }).finally(() => {
        pending -= 1
        if (active) publish({ saving: pending > 0 })
      })
      // A failed save must not poison the queue or discard a later explicit edit.
      tail = operation.catch(() => {})
      return operation
    },
  }
}
